import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** Surface container. */
export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card text-card-foreground shadow-sm',
        className,
      )}
      {...props}
    />
  );
}

/** Card header region. */
export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactNode {
  return <div className={cn('flex flex-col gap-1.5 p-5', className)} {...props} />;
}

/** Card title. Renders an `<h3>` so headings nest correctly under a page `<h1>`/`<h2>`. */
export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>): ReactNode {
  return (
    <h3 className={cn('text-sm font-semibold leading-none tracking-tight', className)} {...props} />
  );
}

/** Supporting copy under a card title. */
export function CardDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>): ReactNode {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

/** Card body. */
export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactNode {
  return <div className={cn('p-5 pt-0', className)} {...props} />;
}

/** Card footer, typically actions. */
export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactNode {
  return <div className={cn('flex items-center gap-2 p-5 pt-0', className)} {...props} />;
}
